/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable class-methods-use-this */

import {
  ADD_SERVICE_PART,
  ADD_SERVICE_PART_FROM_INVENTORY,
  ADD_SERVICE_PARTS,
  CHANGE_SERVICE_PART,
  REMOVE_SERVICE_PART,
  REMOVE_SERVICE_PARTS,
  RESET_SERVICE_PARTS,
  ServicePartActions,
} from '@models';
import * as Services from '@services';
import { DispachedServicePart, InitialServicePart, Order, Service } from '@services';
import { Utils } from '@utils';
import { message } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';

class ServiceStore {
  selectServices: Map<number, Service> = new Map();
  service!: Service | undefined;
  selectedServiceParts: Map<number, DispachedServicePart[]> = new Map();
  additionalServiceParts: Map<number, DispachedServicePart[]> = new Map();
  revisionServiceParts: Map<number, DispachedServicePart[]> = new Map();
  selectedServices: Service[] = [];

  /**
   * Which job the basket above was filled from. The build page mounts this hook once per leg as
   * panels are opened and closed, so "the basket already has something in it" is no longer enough
   * to know it has the right something.
   */
  seededOrderId: string | undefined = undefined;
  additionalSelectedServices: Service[] = [];
  revisionSelectedServices: Service[] = [];
  tenantVat: number = 0;

  services: Map<number, Service> = new Map();
  totalCount = 0;
  skipCount = 0;
  currentPage = 1;
  pageSize = 15;
  loading = false;

  selectLoading = false;
  selectSkipCount = 0;
  selectPageSize = 10;
  selectTotalCount = 0;
  selectScroll = false;

  constructor() {
    makeAutoObservable(this);
  }

  setSelectedServices = (services: Service[]) => {
    this.selectedServices = services;
  };

  setAdditionalSelectedServices = (services: Service[]) => {
    this.additionalSelectedServices = services;
  };

  setTenantVat = (vat: number) => {
    this.tenantVat = vat;
  };

  addService = (id: number) => {
    const service = this.selectServices.get(id);
    if (!service) return;
    this.selectedServices = [...this.selectedServices, service];
    this.dispatchSelectedServiceParts({
      type: ADD_SERVICE_PARTS,
      serviceId: id,
      serviceParts: service.serviceParts.map((part) => ({ ...part, required: false })),
    });
  };

  removeService = (id: number, keepAtLeastOne = false) => {
    if (keepAtLeastOne && this.selectedServices.length === 1) return;
    this.selectedServices = this.selectedServices.filter((x) => x.id !== id);
    this.dispatchSelectedServiceParts({ type: REMOVE_SERVICE_PARTS, serviceId: id });
  };

  clearSelectedServices = () => {
    this.selectedServices = [];
    this.dispatchSelectedServiceParts({ type: RESET_SERVICE_PARTS });
  };

  updateServiceField = (id: number, field: string, value: any) => {
    this.selectedServices = this.selectedServices.map((s) =>
      s.id === id ? { ...s, [field]: value } : s
    );
  };

  addAdditionalService = (id: number) => {
    const service = this.selectServices.get(id);
    if (!service) return;
    this.additionalSelectedServices = [...this.additionalSelectedServices, service];
    this.dispatchAdditionalServiceParts({
      type: ADD_SERVICE_PARTS,
      serviceId: id,
      serviceParts: service.serviceParts.map((part) => ({ ...part, required: false })),
    });
  };

  removeAdditionalService = (id: number) => {
    this.additionalSelectedServices = this.additionalSelectedServices.filter((x) => x.id !== id);
    this.dispatchAdditionalServiceParts({ type: REMOVE_SERVICE_PARTS, serviceId: id });
  };

  clearAdditionalSelectedServices = () => {
    this.additionalSelectedServices = [];
    this.dispatchAdditionalServiceParts({ type: RESET_SERVICE_PARTS });
  };

  updateAdditionalServiceField = (id: number, field: string, value: any) => {
    this.additionalSelectedServices = this.additionalSelectedServices.map((s) =>
      s.id === id ? { ...s, [field]: value } : s
    );
  };

  setRevisionSelectedServices = (services: Service[]) => {
    this.revisionSelectedServices = services;
  };

  addRevisionService = (id: number) => {
    const service = this.selectServices.get(id);
    if (!service) return;
    this.revisionSelectedServices = [...this.revisionSelectedServices, service];
    this.dispatchRevisionServiceParts({
      type: ADD_SERVICE_PARTS,
      serviceId: id,
      serviceParts: service.serviceParts.map((part) => ({ ...part, required: false })),
    });
  };

  removeRevisionService = (id: number, keepAtLeastOne = false) => {
    if (keepAtLeastOne && this.revisionSelectedServices.length === 1) return;
    this.revisionSelectedServices = this.revisionSelectedServices.filter((x) => x.id !== id);
    this.dispatchRevisionServiceParts({ type: REMOVE_SERVICE_PARTS, serviceId: id });
  };

  clearRevisionSelectedServices = () => {
    this.revisionSelectedServices = [];
    this.dispatchRevisionServiceParts({ type: RESET_SERVICE_PARTS });
  };

  updateRevisionServiceField = (id: number, field: string, value: any) => {
    this.revisionSelectedServices = this.revisionSelectedServices.map((s) =>
      s.id === id ? { ...s, [field]: value } : s
    );
  };

  initRevisionFromOrder = (order: Order, vat: number) => {
    this.revisionSelectedServices = order.serviceOrders.map((x) => ({
      ...x,
      id: x.serviceId,
      name: x.service?.name,
      code: x.service?.code,
      normalizedName: x.service?.normalizedName,
      serviceParts: [],
      vat,
      isVat: x.vat > 0,
      isActive: true,
      serviceGroupId: 0,
    }));
    this.revisionServiceParts = new Map();
    order.serviceOrders.forEach((x) => {
      this.dispatchRevisionServiceParts({
        type: ADD_SERVICE_PARTS,
        serviceId: x.serviceId,
        serviceParts: x.extras.map((extra) => ({
          type: extra.type,
          name: extra.name,
          number: extra.number,
          reference: extra.reference,
          quantity: extra.quantity,
          price: extra.price,
          costPrice: extra.costPrice,
          margin: extra.margin,
          discount: extra.discount,
          vat,
          serviceId: x.serviceId,
          inventoryId: extra.inventoryId,
          availableStock: extra.availableStock,
          required: false,
        })),
      });
    });
  };

  addPart = (serviceId: number) => {
    const parts = [...(this.selectedServiceParts.get(serviceId) || [])];
    parts.push({ ...InitialServicePart, serviceId });
    this.selectedServiceParts.set(serviceId, parts);
  };

  removePart = (serviceId: number, index: number) => {
    const parts = [...(this.selectedServiceParts.get(serviceId) || [])];
    parts.splice(index, 1);
    this.selectedServiceParts.set(serviceId, parts);
  };

  updatePart = (serviceId: number, index: number, values: Partial<DispachedServicePart>) => {
    const parts = [...(this.selectedServiceParts.get(serviceId) || [])];
    parts[index] = { ...parts[index], ...values };
    this.selectedServiceParts.set(serviceId, parts);
  };

  // Reflect a returned part live: the order UI reads parts from selectedServiceParts (keyed by
  // serviceId, matched by inventoryId), while returns run against orderStore extras. Keep the
  // visible quantity in sync without forcing a full reload.
  syncReturnedPart = (serviceId: number, inventoryId?: string, remainingQuantity = 0) => {
    if (!inventoryId) return;
    const parts = [...(this.selectedServiceParts.get(serviceId) || [])];
    const index = parts.findIndex((p) => p.inventoryId === inventoryId);
    if (index === -1) return;
    if (remainingQuantity > 0) parts[index] = { ...parts[index], quantity: remainingQuantity };
    else parts.splice(index, 1);
    this.selectedServiceParts.set(serviceId, parts);
  };

  initFromOrder = (order: Order, vat: number) => {
    this.tenantVat = vat;

    // Leave an edit in progress alone - this runs again on every re-render of the job card - but
    // only while the basket belongs to this job. Another job's services get thrown out.
    if (this.selectedServices.length > 0) {
      if (this.seededOrderId === order.id) return;
      this.clearOrderServices();
    }

    this.seededOrderId = order.id;

    this.selectedServices = order.serviceOrders.map((x) => ({
      ...x,
      id: x.serviceId,
      name: x.service?.name,
      code: x.service?.code,
      normalizedName: x.service?.normalizedName,
      serviceParts: [],
      vat,
      isVat: x.vat > 0,
      isActive: true,
      serviceGroupId: 0,
    }));

    order.serviceOrders.forEach((x) => {
      this.dispatchSelectedServiceParts({
        type: ADD_SERVICE_PARTS,
        serviceId: x.serviceId,
        serviceParts: x.extras.map((extra) => ({
          type: extra.type,
          name: extra.name,
          number: extra.number,
          reference: extra.reference,
          quantity: extra.quantity,
          price: extra.price,
          costPrice: extra.costPrice,
          margin: extra.margin,
          discount: extra.discount,
          vat,
          serviceId: x.serviceId,
          inventoryId: extra.inventoryId,
          availableStock: extra.availableStock,
          required: false,
        })),
      });
    });
  };

  clearOrderServices = () => {
    this.selectedServices = [];
    this.selectedServiceParts.clear();
    this.seededOrderId = undefined;
  };

  get pricingSummary() {
    return this._buildPricingSummary(this.selectedServices, this.selectedServiceParts);
  }

  get revisionPricingSummary() {
    return this._buildPricingSummary(this.revisionSelectedServices, this.revisionServiceParts);
  }

  private _buildPricingSummary(
    services: Service[],
    partsMap: Map<number, DispachedServicePart[]>
  ) {
    const labourPrice = services.reduce(
      (acc, curr) => acc + Utils.calculateLabourPrice({ ...curr }),
      0
    );

    const allParts: DispachedServicePart[] = Array.from(partsMap.values()).flat();
    const partsPrice = Utils.calculatePartsPrice(allParts);
    const subTotal = labourPrice + partsPrice;

    const serviceVat = services.reduce(
      (acc, curr) =>
        acc + (curr.isVat ? Utils.calculateVat({ ...curr, vat: curr.isVat ? this.tenantVat : 0 }) : 0),
      0
    );

    const partsVat = allParts.reduce(
      (acc, curr) => acc + Utils.calculateVat({ ...curr, duration: curr.quantity, vat: this.tenantVat }),
      0
    );

    const vatAmount = serviceVat + partsVat;
    const totalPrice = subTotal + vatAmount;

    return { labourPrice, partsPrice, subTotal, vatAmount, totalPrice };
  }

  async getServices(pagedFilterAndSortedRequest: Services.PagedServiceRequest) {
    this.setLoading(true);
    try {
      const result = await Services.GarageService.getAll(pagedFilterAndSortedRequest);
      runInAction(() => {
        result.items.forEach((service: Service) => {
          this.services.set(service.id, service);
        });
        this.totalCount = result.totalCount;
      });
      this.setLoading(false);
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  }

  async getSelectServices(request: Services.PagedSelectServiceRequest) {
    this.setSelectLoading(true);

    //handles search with scroll
    if (request.normalizedName && !this.selectScroll) {
      request.skipCount = 0;
      this.clearSelectServices();
      this.setSelectSkipCount(0);
      this.setSelectScroll(true);
    }

    //handles clear
    if (!request.normalizedName && this.selectScroll) {
      request.skipCount = 0;
      this.clearSelectServices();
      this.setSelectSkipCount(0);
      this.setSelectScroll(false);
    }

    const result = await Services.GarageService.getAll(request);

    runInAction(() => {
      result.items.forEach((service: Service) => {
        this.selectServices.set(service.id, service);
      });
      this.selectTotalCount = result.totalCount;
      this.selectLoading = false;
    });
  }

  /**
   * Full Service rows - including their default parts - for a set of ids.
   *
   * Expanding a template into a build needs the parts, and a template only carries the shortened
   * service (id, name, price, duration), so the catalogue is pulled once and matched by id rather
   * than fetched one service at a time. The results are merged into selectServices so the service
   * pickers inside each expanded job can resolve them without another round trip.
   */
  async loadServicesByIds(ids: number[]): Promise<Service[]> {
    const wanted = Array.from(new Set(ids));
    if (!wanted.length) return [];

    const missing = wanted.filter((id) => !this.selectServices.has(id));

    if (missing.length) {
      try {
        const result = await Services.GarageService.getAll({
          showMyServicesOnly: true,
          maxResultCount: 1000,
          skipCount: 0,
        });
        runInAction(() => {
          result.items.forEach((service: Service) => this.selectServices.set(service.id, service));
        });
      } catch (error) {
        console.error(error);
      }
    }

    // Anything still missing was deleted or belongs to another garage; dropping it is correct.
    return wanted
      .map((id) => this.selectServices.get(id))
      .filter((service): service is Service => !!service);
  }

  async get(entityDto: Services.EntityDto) {
    this.setLoading(true);
    try {
      const result = await Services.GarageService.get(entityDto);
      runInAction(() => {
        this.service = result;
        this.selectedServiceParts.set(result.id, result.serviceParts as DispachedServicePart[]);
      });
      this.setLoading(false);
    } catch (error) {
      console.error(error);
      this.setLoading(false);
    }
  }

  async createPublicService(service: Services.CreateUpdateService) {
    this.setLoading(true);
    try {
      const result = await Services.GarageService.create({
        ...service,
        duration: 0.0,
        price: 0.0,
        isActive: true,
      });
      runInAction(() => {
        if (this.skipCount === 0) {
          this.services.set(result.id, result);
        }
        this.totalCount += 1;
      });
      this.setLoading(false);
      message.success({
        content: 'Successfully created!',
      });
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async createPrivateService(service: Services.CreateUpdateService) {
    this.setLoading(true);
    try {
      const result = await Services.GarageService.create({
        ...service,
        isActive: true,
      });
      runInAction(() => {
        if (this.skipCount === 0) {
          this.services.set(result.id, result);
        }
        this.totalCount += 1;
      });
      this.setLoading(false);
      message.success({
        content: 'Successfully created!',
      });
      // Returned so callers can act on the new id - the template assignment needs it.
      return result;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      return undefined;
    }
  }

  async update(service: Services.CreateUpdateService) {
    this.setLoading(true);
    try {
      const result = await Services.GarageService.update(service);
      runInAction(() => {
        this.services.set(result.id, result);
        this.service = result;
      });
      this.setLoading(false);
      message.success({
        content: 'Successfully updated!',
      });
      return true;
    } catch (error) {
      this.setLoading(false);
      console.error(error);
      return false;
    }
  }

  async delete(entityDto: Services.EntityDto) {
    this.setLoading(true);
    try {
      await Services.GarageService.delete(entityDto);
      runInAction(() => {
        this.services.delete(entityDto.id);
        this.totalCount -= 1;
        this.currentPage =
          this.services.size === 0 && this.currentPage > 0
            ? this.currentPage - 1
            : this.currentPage;
        this.skipCount = this.currentPage > 0 ? (this.currentPage - 1) * 10 : 0;
      });
      this.setLoading(false);
      message.success({
        content: 'Successfully deleted!',
      });
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  async changeActive(id: number) {
    this.setLoading(true);
    try {
      await Services.GarageService.changeActive(id);
      runInAction(() => {
        const service = this.services.get(id);
        if (service) {
          service.isActive = !service.isActive;
        }
      });
      this.setLoading(false);
    } catch (error) {
      this.setLoading(false);
      console.error(error);
    }
  }

  dispatchSelectedServiceParts = (action: ServicePartActions) => {
    this._dispatchParts(this.selectedServiceParts, action);
  };

  dispatchAdditionalServiceParts = (action: ServicePartActions) => {
    this._dispatchParts(this.additionalServiceParts, action);
  };

  dispatchRevisionServiceParts = (action: ServicePartActions) => {
    this._dispatchParts(this.revisionServiceParts, action);
  };

  addPartsFromInventoryBulk = (
    createdItems: any[],
    parsedItems: any[] | undefined,
    variant: 'selected' | 'additional' | 'revision' = 'selected',
    defaultServiceId?: number
  ) => {
    const dispatch =
      variant === 'additional'
        ? this.dispatchAdditionalServiceParts
        : variant === 'revision'
          ? this.dispatchRevisionServiceParts
          : this.dispatchSelectedServiceParts;
    createdItems.forEach((item, idx) => {
      const parsedItem = parsedItems?.[idx];
      const targetServiceId = parsedItem?.serviceId ?? defaultServiceId;
      if (targetServiceId === undefined || targetServiceId === null) return;
      const qty = parsedItem?.quantity ?? item.quantity ?? 1;
      const costPrice = parsedItem?.costPrice ?? item.costPrice;
      const sellPrice = parsedItem?.sellPrice ?? item.sellPrice;
      dispatch({
        type: ADD_SERVICE_PART_FROM_INVENTORY,
        serviceId: targetServiceId,
        servicePart: {
          name: item.name,
          number: item.partNumber,
          reference: item.reference,
          costPrice,
          price: sellPrice,
          quantity: qty,
          margin: sellPrice > 0 ? ((sellPrice - costPrice) / sellPrice) * 100 : 0,
          type: item.categoryName || 'Part',
          inventoryId: item.id,
          required: false,
        },
      });
    });
  };

  private _dispatchParts = (map: Map<number, DispachedServicePart[]>, action: ServicePartActions) => {
    switch (action.type) {
      case ADD_SERVICE_PART:
        runInAction(() => {
          const parts = map.get(action.serviceId!) || [];
          map.set(action.serviceId!, [...parts, { ...InitialServicePart, serviceId: action.serviceId }]);
        });
        return;
      case ADD_SERVICE_PART_FROM_INVENTORY:
        runInAction(() => {
          const parts = map.get(action.serviceId) || [];
          const incomingInventoryId = action.servicePart?.inventoryId;
          const existingIdx = incomingInventoryId
            ? parts.findIndex((p) => p.inventoryId === incomingInventoryId)
            : -1;
          if (existingIdx >= 0) {
            const existing = parts[existingIdx];
            const incomingQty = action.servicePart?.quantity ?? 1;
            const merged = {
              ...existing,
              quantity: (existing.quantity ?? 0) + incomingQty,
            };
            const next = [...parts];
            next[existingIdx] = merged;
            map.set(action.serviceId, next);
          } else {
            map.set(action.serviceId, [...parts, { ...action.servicePart, serviceId: action.serviceId }]);
          }
        });
        return;
      case ADD_SERVICE_PARTS:
        runInAction(() => {
          map.set(action.serviceId!, action.serviceParts);
        });
        return;
      case REMOVE_SERVICE_PART:
        runInAction(() => {
          const parts = [...(map.get(action.serviceId!) || [])];
          parts.splice(action.index, 1);
          map.set(action.serviceId!, parts);
        });
        return;
      case REMOVE_SERVICE_PARTS:
        runInAction(() => {
          map.delete(action.serviceId!);
        });
        return;
      case CHANGE_SERVICE_PART:
        runInAction(() => {
          const parts = [...(map.get(action.serviceId!) || [])];
          parts[action.index] = { ...parts[action.index], ...action.servicePart };
          map.set(action.serviceId!, parts);
        });
        return;
      case RESET_SERVICE_PARTS:
        runInAction(() => {
          map.clear();
        });
        return;
      default:
        return;
    }
  };

  handlePageChange = (pagination: any) => {
    runInAction(() => {
      this.pageSize = pagination.pageSize;
      this.skipCount = (pagination.current - 1) * pagination.pageSize;
      this.currentPage = pagination.current;
    });
  };

  setLoading = (value: boolean) => {
    runInAction(() => {
      this.loading = value;
    });
  };

  setSelectLoading = (value: boolean) => {
    runInAction(() => {
      this.selectLoading = value;
    });
  };

  clearService = () => {
    runInAction(() => {
      this.service = undefined;
    });
  };

  clearServices = () => {
    runInAction(() => {
      this.services.clear();
      this.totalCount = 0;
    });
  };

  clearSelectServices = () => {
    runInAction(() => {
      this.selectServices.clear();
      this.selectTotalCount = 0;
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };

  setSelectSkipCount = (value: number) => {
    runInAction(() => {
      this.selectSkipCount = value;
    });
  };

  setSelectScroll = (value: boolean) => {
    runInAction(() => {
      this.selectScroll = value;
    });
  };
}

export default ServiceStore;
